// Singleton Supabase client for Fyrefly. All app code that needs to
// talk to Supabase imports from here; per ARCHITECTURE.md §7 we never
// import `@supabase/supabase-js` directly anywhere else.
//
// When the env vars are missing (e.g. dev without `.env.local`,
// preview deployments without secrets), `supabase` is null. Callers
// must guard. The dual-mode stores treat null as "local-only mode";
// the UI's account section explains why sign-in is unavailable.

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export const supabaseConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

// Wall-clock timeout for every Supabase REST/RPC call. Without this,
// PostgREST requests on a flaky LTE connection hang for the browser's
// default fetch timeout (≥50 s on iOS Safari), which freezes the UI
// thread waiting on a write that will never complete. 12 s is plenty
// of headroom for normal queries and well under what triggers a
// pilot-noticeable wait. HI-3 audit 2026-05-22.
const SUPABASE_FETCH_TIMEOUT_MS = 12_000;

function timeoutFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  // Respect a caller-supplied AbortSignal by chaining ours to it.
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), SUPABASE_FETCH_TIMEOUT_MS);
  if (init.signal) {
    if (init.signal.aborted) ctrl.abort();
    else init.signal.addEventListener('abort', () => ctrl.abort(), { once: true });
  }
  return fetch(input, { ...init, signal: ctrl.signal }).finally(() => clearTimeout(timer));
}

// Setting `db.schema: 'app'` narrows the SupabaseClient generic to
// schema 'app', which makes a `SupabaseClient | null` annotation
// incompatible with the inferred type. Letting TS infer keeps the
// type accurate (and `.schema('public')` is still available per-call
// for reads against reference tables like NASR / aviation data).
export const supabase = supabaseConfigured
  ? createClient(SUPABASE_URL!, SUPABASE_ANON_KEY!, {
      auth: {
        // Persist + auto-refresh sessions across reloads.
        persistSession: true,
        autoRefreshToken: true,
        // Detect magic-link / OAuth redirects so the session completes
        // on whatever route the user lands on.
        detectSessionInUrl: true,
        flowType: 'pkce',
      },
      db: {
        // User-data tables live in `app`. Per-call escape hatch via
        // `supabase.schema('public').from('airports')` etc.
        schema: 'app',
      },
      global: {
        // Bounded fetch on every Supabase REST/RPC call so a stalled
        // LTE connection can't park the UI thread for 50 s.
        fetch: timeoutFetch,
      },
    })
  : null;
